"use client"

import { useEffect, useRef } from "react"
import { useEditorStore } from "@/lib/editor-store"
import {
  createTabSyncChannel,
  type DetachedStateSnapshot,
  type TabSyncMessage,
} from "@/lib/detached-tab-sync"
import { setActiveDetachedWindow } from "@/lib/detached-window-utils"

function buildSnapshot(
  state: ReturnType<typeof useEditorStore>["state"],
): DetachedStateSnapshot {
  return {
    sessionId: state.sessionId,
    sessionPhase: state.sessionPhase,
    mainFile: state.mainFile,
    activeFile: state.activeFile,
    autoCompile: state.autoCompile,
    files: state.files,
    events: state.events,
    pdfUrl: state.pdfUrl,
    pdfByFile: state.pdfByFile,
    compiledFile: state.compiledFile,
    compileStatus: state.compileStatus,
    compileError: state.compileError,
    compileErrorDetails: state.compileErrorDetails,
    wsStatus: state.wsStatus,
  }
}

/**
 * Main-window bridge: pushes store updates to detached windows and applies
 * edits/compile/synctex requests coming back from detached windows.
 */
export function DetachedWindowSync() {
  const {
    state,
    updateContent,
    setActiveFile,
    manualCompile,
    applySynctexForwardResult,
    applyInverseSyncResult,
    openFileAtLine,
  } = useEditorStore()

  const detachedTabsRef = useRef(new Set<string>())
  const stateRef = useRef(state)
  stateRef.current = state

  const storeApiRef = useRef({
    updateContent,
    setActiveFile,
    manualCompile,
    applySynctexForwardResult,
    applyInverseSyncResult,
    openFileAtLine,
  })
  storeApiRef.current = {
    updateContent,
    setActiveFile,
    manualCompile,
    applySynctexForwardResult,
    applyInverseSyncResult,
    openFileAtLine,
  }

  const broadcastSnapshot = (tabId: string) => {
    const channel = createTabSyncChannel()
    if (!channel) return
    channel.postMessage({
      type: "STATE_SYNC",
      tabId,
      snapshot: buildSnapshot(stateRef.current),
    } satisfies TabSyncMessage)
    channel.close()
  }

  useEffect(() => {
    const channel = createTabSyncChannel()
    if (!channel) return

    channel.onmessage = (event: MessageEvent<TabSyncMessage>) => {
      const msg = event.data
      const api = storeApiRef.current

      switch (msg.type) {
        case "TAB_DETACHED":
        case "DETACHED_READY":
          detachedTabsRef.current.add(msg.tabId)
          broadcastSnapshot(msg.tabId)
          break
        case "DETACHED_WINDOW_READY":
          setActiveDetachedWindow(msg.windowId)
          for (const tabId of msg.tabIds) {
            detachedTabsRef.current.add(tabId)
            broadcastSnapshot(tabId)
          }
          break
        case "DETACHED_WINDOW_CLOSED":
          setActiveDetachedWindow(null)
          break
        case "DETACHED_CLOSED":
          detachedTabsRef.current.delete(msg.tabId)
          break
        case "FILE_EDIT":
          api.updateContent(msg.fileId, msg.content)
          break
        case "SET_ACTIVE_FILE":
          api.setActiveFile(msg.fileId)
          break
        case "MANUAL_COMPILE":
          api.manualCompile()
          break
        case "SYNCTEX_FORWARD":
          if (msg.sourceTabId !== "main") {
            api.applySynctexForwardResult(msg.box)
          }
          break
        case "SYNCTEX_INVERSE":
          if (msg.sourceTabId !== "main") {
            api.applyInverseSyncResult(msg.line, msg.column, msg.word)
          }
          break
        case "OPEN_FILE_AT_LINE":
          if (msg.sourceTabId !== "main") {
            api.openFileAtLine(msg.fileId, msg.fileName, msg.line)
          }
          break
        default:
          break
      }
    }

    return () => channel.close()
  }, [])

  useEffect(() => {
    const tabs = detachedTabsRef.current
    if (tabs.size === 0) return

    const channel = createTabSyncChannel()
    if (!channel) return

    const snapshot = buildSnapshot(state)
    for (const tabId of tabs) {
      channel.postMessage({ type: "STATE_SYNC", tabId, snapshot } satisfies TabSyncMessage)
    }
    channel.close()
  }, [
    state.files,
    state.events,
    state.pdfUrl,
    state.pdfByFile,
    state.compiledFile,
    state.compileStatus,
    state.compileError,
    state.compileErrorDetails,
    state.sessionId,
    state.wsStatus,
    state.activeFile,
    state.sessionPhase,
    state.mainFile,
    state.autoCompile,
  ])

  return null
}
